import Typography from '@navi/web-ui/lib/primitives/Typography/index';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { formatAmount } from 'src/utils/commonUtils';
import { getEMIBreakUp } from '../../actions/casesDetailsActions';
import { EMIBreakUpSubType } from '../../constants/EmiSchedule.constant';
import styles from './index.module.scss';
import Overlay from 'src/components/Overlay/Overlay';
import CloseIcon from '@navi/web-ui/lib/icons/CloseIcon/CloseIcon';
import TableLoader from 'src/components/TableLoader/TableLoader';

interface IEmiBreakup {
  refId: string;
  isOpen: boolean;
  toggleOverlay: any;
}

const EmiBreakupOverlay: React.FC<IEmiBreakup> = props => {
  const { refId, isOpen, toggleOverlay } = props;
  const { loanId = '', customerId = '' } = useParams();
  const dispatch = useDispatch();
  const emiBreakup = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.emiBreakup?.[refId]?.data
  );
  const loading = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.emiBreakup?.[refId]?.loading
  );
  const error = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.emiBreakup?.[refId]?.error
  );

  useEffect(() => {
    if (!emiBreakup && refId) dispatch(getEMIBreakUp(loanId, customerId, refId));
  }, [refId]);

  useEffect(() => {
    if (error) toggleOverlay();
  }, [error]);

  const renderContent = useMemo(() => {
    if (!emiBreakup?.length)
      return (
        <div className={styles.errorState}>
          <Typography variant={'h6'}>Something went wrong</Typography>
        </div>
      );
    return emiBreakup?.map(emi => {
      return (
        <div key={emi.referenceId} className={styles['content']}>
          <Typography className={styles.greyScale1} variant={'p3'}>
            {EMIBreakUpSubType[emi.subType]}
          </Typography>
          <Typography className={styles.greyScale1} variant={'p3'}>
            {formatAmount(emi.amount, false)}
          </Typography>
          <Typography className={styles.red} variant={'p3'}>
            {formatAmount(emi.unpaidDueAmount, false)}
          </Typography>
        </div>
      );
    });
  }, [emiBreakup]);

  return (
    <div className={styles['emiBreakupAction']}>
      {isOpen && (
        <Overlay visible={isOpen}>
          <div className={styles['emiBreakupActionOverlay']}>
            <header>
              <Typography variant={'h3'}>EMI Breakup</Typography>
              <CloseIcon onClick={toggleOverlay} />
            </header>
            <div className={styles.content}>
              <Typography variant={'p3'}>Breakup</Typography>
              <Typography variant={'p3'}>Amount</Typography>
              <Typography variant={'p3'}>Unpaid</Typography>
            </div>
            <SuspenseLoader loading={!!loading} fallBack={<TableLoader numberOfLines={3} />}>
              {renderContent}
            </SuspenseLoader>
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default EmiBreakupOverlay;
